import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { subscriptionAPI } from '../../api';

function UserInvitations() {
  const [invitations, setInvitations] = useState([]);
  const [activeTab, setActiveTab] = useState('invitations');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch pending invitations
      const invitationsRes = await subscriptionAPI.getInvitations();
      console.log('Invitations response:', invitationsRes);
      
      if (invitationsRes.data?.invitations) {
        setInvitations(invitationsRes.data.invitations || []);
      } else if (invitationsRes.data && Array.isArray(invitationsRes.data)) {
        setInvitations(invitationsRes.data || []);
      } else {
        setInvitations([]);
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.data?.detail || 
                          error.message || 
                          'Failed to load subscription data';
      toast.error(errorMessage);
      setInvitations([]);
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    setIsLoading(true);
    try {
      const response = await subscriptionAPI.acceptInvitation(invitationId);
      console.log('Accept invitation response:', response);
      
      if (response.success || response.message) {
        const successMessage = response.message || 'Invitation accepted!';
        toast.success(successMessage);
        fetchData(); // Refresh the list
      } else {
        const errorMessage = response.error || response.detail || 'Failed to accept invitation';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.data?.detail || 
                          error.message || 
                          'Failed to accept invitation';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // const handleRejectInvitation = async (invitationId) => {
  //   if (!window.confirm('Are you sure you want to reject this invitation?')) return;
    
  //   setIsLoading(true);
  //   try {
  //     // Note: Backend doesn't have reject endpoint yet
  //     // You should implement this endpoint in backend
  //     toast.info('Reject functionality not implemented yet');
  //     // For now, just remove from frontend
  //     setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
  //   } catch (error) {
  //     console.error('Error rejecting invitation:', error);
  //     const errorMessage = error.response?.data?.detail || 
  //                         error.data?.detail || 
  //                         error.message || 
  //                         'Failed to reject invitation';
  //     toast.error(errorMessage);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };


  const handleRejectInvitation = async (userId) => {
  if (!window.confirm('Are you sure you want to remove this user from your subscription?')) return;

  try {
    // Call the backend API to remove the user
    const response = await subscriptionAPI.removeSubscriptionUser(userId);
    
    if (response.message) {
      toast.success(response.message);
      // Refresh the user list
      fetchData();
    } else {
      toast.error('Failed to remove user');
    }
  } catch (error) {
    console.error('Error removing user:', error);
    const errorMessage = error.response?.data?.detail || 
                        error.data?.detail || 
                        error.message || 
                        'Failed to remove user';
    toast.error(errorMessage);
  }
};

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <ToastContainer />
      
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">My Subscription Invitations</h1>
        <p className="text-gray-600 mt-2">Manage subscription invitations you've received</p>
      </div>

      {/* Invitations Tab */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Pending Invitations ({invitations.length})
          </h2>
          {invitations.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <i className="fas fa-envelope-open-text text-gray-400 text-3xl"></i>
              </div>
              <p className="text-gray-500 text-lg mb-2">No pending invitations</p>
              <p className="text-gray-400 text-sm">You don't have any subscription invitations at the moment.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {invitations.map((invitation) => (
                <div key={invitation.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-start mb-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                          <i className="fas fa-user-tag text-blue-600"></i>
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-800 text-lg">
                            Invitation from {invitation.owner_name || 'Subscription Owner'}
                          </h3>
                          <p className="text-gray-600">{invitation.owner_email}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-500">
                        <div className="flex items-center">
                          <i className="fas fa-calendar-alt text-blue-500 mr-2"></i>
                          <span>Invited: {new Date(invitation.invited_at || invitation.created_at).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-clock text-amber-500 mr-2"></i>
                          <span>Expires: {new Date(invitation.expires_at).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-info-circle text-green-500 mr-2"></i>
                          <span>Status: <span className="capitalize">{invitation.status || 'pending'}</span></span>
                        </div>
                        {invitation.subscription_active !== undefined && (
                          <div className="flex items-center">
                            <i className="fas fa-bolt text-purple-500 mr-2"></i>
                            <span>Subscription: {invitation.subscription_active ? 'Active' : 'Inactive'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
                      <button
                        onClick={() => handleAcceptInvitation(invitation.id)}
                        disabled={isLoading || invitation.status !== 'pending'}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-semibold disabled:opacity-50 transition-colors duration-200 flex items-center justify-center"
                      >
                        <i className="fas fa-check mr-2"></i>
                        Accept Invitation
                      </button>
                      <button
                        onClick={() => handleRejectInvitation(invitation.id)}
                        disabled={isLoading}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-semibold disabled:opacity-50 transition-colors duration-200 flex items-center justify-center"
                      >
                        <i className="fas fa-times mr-2"></i>
                        Reject
                      </button>
                    </div>
                  </div>
                  
                  {invitation.status === 'pending' && invitation.expires_at && new Date(invitation.expires_at) < new Date() && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm flex items-center">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        This invitation has expired
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-blue-800 mb-3 flex items-center">
          <i className="fas fa-info-circle mr-2"></i>
          How subscription sharing works:
        </h3>
        <ul className="list-disc list-inside text-blue-700 space-y-2">
          <li>Accept invitations to join someone else's subscription plan</li>
          <li>You'll get access to premium features while the subscription is active</li>
          <li>You can leave a subscription anytime from your dashboard</li>
          <li>If the owner cancels their subscription, all shared users lose access</li>
          <li>You can only be part of one subscription at a time</li>
          <li>Invitations expire after 7 days if not accepted</li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="mt-8">
        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href="https://wa.me/2348113315252"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white py-3.5 px-6 rounded-xl font-semibold transition-colors duration-300 w-full sm:w-auto sm:flex-1"
          >
            <i className="fab fa-whatsapp text-lg"></i>
            <span>Chat on WhatsApp</span>
          </a>

          <button
            onClick={() => navigate('/cbt_creator/dashboard')}
            className="group flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white py-3.5 px-6 rounded-xl font-semibold transition-colors duration-300 w-full sm:w-auto sm:flex-1"
          >
            <i className="fas fa-arrow-left text-lg"></i>
            <span>Back to Dashboard</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserInvitations;